import React from 'react';

// Gaussian kernel function for KDE smoothing
const gaussian = (x, mean, sigma) =>
  Math.exp(-0.5 * Math.pow((x - mean) / sigma, 2));

/**
 * Data-driven Bell Curve SVG — shape reflects actual KRA performance distribution.
 * Renders a smoothed (KDE) curve over the 1-5 rating scale with three zones:
 *   - Rating 1 & 2 → Bottom 15% (red)
 *   - Rating 3     → Middle 70% (purple)
 *   - Rating 4 & 5 → Top 15% (green)
 *
 * Props:
 *   data    — { total, actual: { top, middle, bottom, by_rating } }
 *   isDark  — boolean theme flag
 */
export const BellCurveSVG = ({ data, isDark }) => {
  if (!data || !data.total) return null;

  const W = 700, H = 340;
  const pad = { top: 10, bottom: 70, left: 30, right: 30 };
  const cW = W - pad.left - pad.right;
  const cH = H - pad.top - pad.bottom;
  const baseY = pad.top + cH;

  const total = data.total;
  const bottomCount = data.actual?.bottom?.count || 0;
  const middleCount = data.actual?.middle?.count || 0;
  const topCount = data.actual?.top?.count || 0;
  const bottomPct = data.actual?.bottom?.pct || 0;
  const middlePct = data.actual?.middle?.pct || 0;
  const topPct = data.actual?.top?.pct || 0;
  const byRating = data.actual?.by_rating || {};

  const counts = [
    0,
    parseInt(byRating['1'] || 0),
    parseInt(byRating['2'] || 0),
    parseInt(byRating['3'] || 0),
    parseInt(byRating['4'] || 0),
    parseInt(byRating['5'] || 0),
  ];

  const bandwidth = 0.5;
  const numPts = 200;
  const pts = [];
  let maxY = 0;
  for (let i = 0; i <= numPts; i++) {
    const t = i / numPts;
    const x = 0.3 + t * 4.4;
    let density = 0;
    for (let r = 1; r <= 5; r++) {
      density += counts[r] * gaussian(x, r, bandwidth);
    }
    if (density > maxY) maxY = density;
    pts.push({ x, density });
  }
  if (maxY === 0) maxY = 1;

  const toSvgX = (v) => pad.left + ((v - 0.3) / 4.4) * cW;
  const toSvgY = (v) => pad.top + cH - (v / maxY) * cH * 0.88;

  const z1x = toSvgX(2.5);
  const z2x = toSvgX(3.5);

  const curvePath = pts
    .map((p, i) => {
      const sx = toSvgX(p.x);
      const sy = toSvgY(p.density);
      return i === 0 ? `M ${sx} ${sy}` : `L ${sx} ${sy}`;
    })
    .join(' ');

  const makeZonePath = (startX, endX) => {
    const zonePts = pts.filter(
      (p) => toSvgX(p.x) >= startX && toSvgX(p.x) <= endX
    );
    if (zonePts.length === 0) return '';
    let path = `M ${Math.max(startX, toSvgX(zonePts[0].x))} ${baseY}`;
    zonePts.forEach((p) => {
      path += ` L ${toSvgX(p.x)} ${toSvgY(p.density)}`;
    });
    path += ` L ${Math.min(endX, toSvgX(zonePts[zonePts.length - 1].x))} ${baseY} Z`;
    return path;
  };

  const zone1Path = makeZonePath(pad.left, z1x);
  const zone2Path = makeZonePath(z1x, z2x);
  const zone3Path = makeZonePath(z2x, W - pad.right);

  const textColor = isDark ? '#f1f5f9' : '#0f172a';
  const subColor = isDark ? '#94a3b8' : '#64748b';
  const lineColor = isDark ? '#334155' : '#d1d5db';

  const ratingPositions = [1, 2, 3, 4, 5].map((r) => ({ r, x: toSvgX(r) }));
  const ratingLabels = {
    1: 'Concern',
    2: 'Needs Imp.',
    3: 'Reliable',
    4: 'Strong',
    5: 'Outstanding',
  };
  const ratingColors = {
    1: '#ef4444',
    2: '#f97316',
    3: '#331062',
    4: '#3b82f6',
    5: '#10b981',
  };

  const getZonePeakY = (startR, endR) => {
    const zonePts = pts.filter((p) => p.x >= startR && p.x <= endR);
    if (zonePts.length === 0) return baseY - 40;
    const peak = Math.max(...zonePts.map((p) => p.density));
    return toSvgY(peak);
  };

  const bottomLabelY = Math.max(getZonePeakY(0.3, 2.5) - 10, pad.top + 8);
  const middleLabelY = Math.max(getZonePeakY(2.5, 3.5) - 10, pad.top + 8);
  const topLabelY = Math.max(getZonePeakY(3.5, 4.7) - 10, pad.top + 8);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      style={{ maxHeight: '340px' }}
      data-testid="bell-curve-svg"
    >
      <path d={zone1Path} fill="#ef4444" fillOpacity="0.18" />
      <path d={zone2Path} fill="#7c3aed" fillOpacity="0.15" />
      <path d={zone3Path} fill="#10b981" fillOpacity="0.18" />

      <path
        d={curvePath}
        fill="none"
        stroke={isDark ? '#e2e8f0' : '#1e293b'}
        strokeWidth="3"
        strokeLinejoin="round"
      />

      <line x1={pad.left} y1={baseY} x2={W - pad.right} y2={baseY} stroke={lineColor} strokeWidth="1.5" />

      <line x1={z1x} y1={pad.top + 20} x2={z1x} y2={baseY} stroke={lineColor} strokeWidth="1" strokeDasharray="6,4" />
      <line x1={z2x} y1={pad.top + 20} x2={z2x} y2={baseY} stroke={lineColor} strokeWidth="1" strokeDasharray="6,4" />

      <rect x={(pad.left + z1x) / 2 - 50} y={bottomLabelY - 6} width="100" height="42" rx="6"
            fill={isDark ? '#1e293b' : '#fff'} stroke="#ef4444" strokeWidth="1.5" fillOpacity="0.95" />
      <text x={(pad.left + z1x) / 2} y={bottomLabelY + 10} textAnchor="middle" fontSize="13" fontWeight="800" fill="#ef4444">
        {`${bottomPct}%`}
      </text>
      <text x={(pad.left + z1x) / 2} y={bottomLabelY + 24} textAnchor="middle" fontSize="10" fill={subColor}>
        {`${bottomCount} of ${total} (Target 15%)`}
      </text>

      <rect x={(z1x + z2x) / 2 - 50} y={middleLabelY - 6} width="100" height="42" rx="6"
            fill={isDark ? '#1e293b' : '#fff'} stroke="#7c3aed" strokeWidth="1.5" fillOpacity="0.95" />
      <text x={(z1x + z2x) / 2} y={middleLabelY + 10} textAnchor="middle" fontSize="13" fontWeight="800" fill="#7c3aed">
        {`${middlePct}%`}
      </text>
      <text x={(z1x + z2x) / 2} y={middleLabelY + 24} textAnchor="middle" fontSize="10" fill={subColor}>
        {`${middleCount} of ${total} (Target 70%)`}
      </text>

      <rect x={(z2x + W - pad.right) / 2 - 50} y={topLabelY - 6} width="100" height="42" rx="6"
            fill={isDark ? '#1e293b' : '#fff'} stroke="#10b981" strokeWidth="1.5" fillOpacity="0.95" />
      <text x={(z2x + W - pad.right) / 2} y={topLabelY + 10} textAnchor="middle" fontSize="13" fontWeight="800" fill="#10b981">
        {`${topPct}%`}
      </text>
      <text x={(z2x + W - pad.right) / 2} y={topLabelY + 24} textAnchor="middle" fontSize="10" fill={subColor}>
        {`${topCount} of ${total} (Target 15%)`}
      </text>

      {ratingPositions.map(({ r, x }) => (
        <g key={r}>
          <line x1={x} y1={baseY} x2={x} y2={baseY + 6} stroke={lineColor} strokeWidth="1" />
          <text x={x} y={baseY + 20} textAnchor="middle" fontSize="14" fontWeight="700" fill={textColor}>
            {String(r)}
          </text>
          <text x={x} y={baseY + 33} textAnchor="middle" fontSize="9" fill={subColor}>
            {ratingLabels[r]}
          </text>
          <text x={x} y={baseY + 47} textAnchor="middle" fontSize="11" fontWeight="700" fill={ratingColors[r]}>
            {String(byRating[String(r)] || 0)}
          </text>
        </g>
      ))}

      <text x={(pad.left + z1x) / 2} y={baseY + 62} textAnchor="middle" fontSize="10" fontWeight="600" fill="#ef4444">
        Rating 1 &amp; 2
      </text>
      <text x={(z1x + z2x) / 2} y={baseY + 62} textAnchor="middle" fontSize="10" fontWeight="600" fill="#7c3aed">
        Rating 3
      </text>
      <text x={(z2x + W - pad.right) / 2} y={baseY + 62} textAnchor="middle" fontSize="10" fontWeight="600" fill="#10b981">
        Rating 4 &amp; 5
      </text>
    </svg>
  );
};

export default BellCurveSVG;
